import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Button from "../common/button/button";
import Style from "./orderMobile.module.scss";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "../reusable/loadingIndicator";
import { RootState } from "../reducers";
import { createOrder } from "../actions/ticket";
import GenericMobileCard from "./general/genericMobileCard";
import InputFieldWithLabel from "../common/input/inputFieldWithLabel";
import CountrySelector, { convertLanguageCode }  from "./general/CountrySelector";
import LabelWithHR from "../common/label/labelWithHR";

const OrderBillMobile = () => {
  const { handleSubmit, register, control, watch, setError, formState: { errors } } = useForm();
  const { isLoading } = useSelector((state: RootState) => state.loader);
  const [order, setOrder] = useState({
    firstName: "",
    lastName: "",
    street: "",
    city: "",
    postalCode: "",
    email: "",
    country: "",
  });
  const ticket = useSelector((state: RootState) => state.ticket.ticket);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const appLanguageCode = localStorage.getItem("beppoSelectedLanguage") || "deu";
  const apiLanguageCode = convertLanguageCode(appLanguageCode, 'three-letter');

  const [sendPost, setSendPost] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>("");


  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const street = watch("street");
  const postalCode = watch("postalCode");
  const city = watch("city");
  const email = watch("email");

  const onSubmit = async (data: any) => {
    const handleError = (error: string) => {
      setError("firstName", { type: "manual", message: error });
    };

    if (sendEmail && !data.email) {
      setError("email", {
        type: "manual",
        message: t("Order.Email is required"),
      });
      return;
    }

    dispatch(
      createOrder(
        {
          firstName: data.firstName,
          lastName: data.lastName,
          country: selectedCountry,
          street: data.street,
          city: data.city,
          postalCode: data.postalCode,
          email: sendEmail ? data.email : undefined,
          sendPost,
          sendEmail,
          isViolatorAddressDetermined: ticket.violatorAddress?.status === "DETERMINED",
        },
        ticket,
        handleError,
        () => {},
        () => {}
      )
    );
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <GenericMobileCard title={t("Order.Order a payment slip")}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={Style.form__flex}>
          <div className={Style.columnDirectionFirstName}>
            <InputFieldWithLabel
              style={{ marginRight: "5px" }}
              label={t("Order.First name")}
              registerName="firstName"
              name="firstName"
              register={register}
              required
              type="text"
            />
          </div>
          <div className={Style.columnDirectionLastName}>
            <InputFieldWithLabel
              style={{ marginRight: "5px" }}
              label={t("Order.Last name")}
              registerName="lastName"
              name="lastName"
              register={register}
              required
              type="text"
            />
          </div>
        </div>

        <div>
          <LabelWithHR label={t("Order.Country")} />
          <Controller
            name="country"
            control={control}
            render={() => (
              <CountrySelector
                selectedCountry={selectedCountry}
                onCountryChange={(country) => setSelectedCountry(country.code)}
                t={t}
                apiLanguageCode={apiLanguageCode}
                className={Style.countryDropdown}
              />
            )}
          />
        </div>

        <InputFieldWithLabel
          style={{ marginRight: "5px" }}
          label={t("Order.Street and house number")}
          name="street"
          registerName="street"
          register={register}
          required
          type="text"
        />

        <InputFieldWithLabel
            style={{ marginRight: "5px" }}
          label={t("Order.Post code")}
          registerName="postalCode"
          name="postalCode"
          register={register}
          required
          type="text"
        />

        <InputFieldWithLabel
            style={{ marginRight: "5px" }}
          label={t("Order.City")}
          name="city"
          registerName="city"
          register={register}
          required
          type="text"
        />

        <div className={Style['checkbox-container']}>
          <label className={Style['checkbox-option']}>
            <input
              type="checkbox"
              checked={sendPost}
              onChange={() => {
                    if (!sendPost) {
                      setSendPost(true);
                      setSendEmail(false);
                    } else {
                      setSendPost(false);
                    }
                }
              }
            />
            {t("Order.Send by post")}
          </label>
          <label className={Style['checkbox-option']}>
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={() => {
                    if (!sendEmail) {
                      setSendEmail(true);
                      setSendPost(false);
                    } else {
                      setSendEmail(false);
                    }
                }
              }
            />
            {t("Order.Send by email")}
          </label>
        </div>

        {sendEmail && (
          <InputFieldWithLabel
            label={t("Order.Email")}
            style={{ marginRight: "5px" }}
            name="email"
            registerName="email"
            register={register}
            required
            type="email"
          />
        )}

        <Button
          text={t("Order.Order")}
          disabled={
            !firstName || !lastName || !street || !postalCode || !city || !selectedCountry ||
            (sendEmail && !email) || (!sendEmail && !sendPost)
          }
          classNameWrapper={Style.buttonMobile}
        />
      </form>
    </GenericMobileCard>
  );
};

export default OrderBillMobile;
